import createDecorator from 'final-form-calculate';
import _ from 'lodash';

import {
    getBankRoutingNumberAllowedLengthByCountry,
    isPaymentModeApplicableForCountry,
} from '../../commons/CountryUtils';
import { validateSwiftCodeUtil } from '../../commons/utils';
import { PAYMENT_MODE } from '../InvoicingConstants';

export const TotalCallAmountCalculateDecorator = createDecorator({
    field: 'callDuration',
    updates: {
        totalAmount: (callDuration, formValues) => {
            const billingRate = _.get(
                formValues,
                'consultantInvoiceRateOfCompensation.billingRate'
            );
            return calculateTotalInvoiceCost(billingRate, callDuration);
        },
    },
});

export const calculateTotalInvoiceCost = (billingRate, callDuration) => {
    if (!callDuration || Number.isNaN(callDuration) || !billingRate) return 0;
    return (callDuration * (billingRate / 60)).toFixed(2);
};

const validateCallDuration = (callDuration) => {
    const MAX_CALL_DURATION = 999; // in minutes
    let callDurationError;
    if (callDuration && (callDuration < 0 || callDuration > MAX_CALL_DURATION))
        callDurationError = `Please enter call duration between 0-${MAX_CALL_DURATION} minutes`;
    return callDurationError;
};

/* eslint-disable consistent-return */
const validateBankDetails = (bankDetails, consultantCountry) => {
    if (
        !isPaymentModeApplicableForCountry(
            consultantCountry,
            PAYMENT_MODE.BANK_PAYMENT
        )
    ) {
        return;
    }

    const bankDetailsError = validateAccountNumber(bankDetails);

    // validating bank routing number
    const bankRoutingNumber = _.trim(bankDetails?.bankRoutingNumber);
    const allowedBankRoutingNumberLength =
        getBankRoutingNumberAllowedLengthByCountry(consultantCountry);
    if (
        bankRoutingNumber &&
        // eslint-disable-next-line no-restricted-globals, unicorn/prefer-number-properties
        (isNaN(bankRoutingNumber) ||
            !_.isEqual(
                bankRoutingNumber.length,
                allowedBankRoutingNumberLength
            ))
    ) {
        bankDetailsError.bankRoutingNumber = `Please enter a valid ${allowedBankRoutingNumberLength}-digit number.`;
    }

    return bankDetailsError;
};

const validateWireTransferDetails = (
    wireTransferDetails,
    consultantCountry
) => {
    if (
        !isPaymentModeApplicableForCountry(
            consultantCountry,
            PAYMENT_MODE.WIRE_TRANSFER
        )
    ) {
        return;
    }

    const wireTransferDetailsError = validateAccountNumber(wireTransferDetails);

    // validating SWIFT code
    const swiftCode = wireTransferDetails?.swiftCode;
    if (!validateSwiftCodeUtil(swiftCode)) {
        wireTransferDetailsError.swiftCode = 'Please enter a valid SWIFT code.';
    }

    // validating address fields for wire transfer
    const beneficiaryAddress = wireTransferDetails?.beneficiaryAddress;
    const beneficiaryAddressError = {};

    if (!beneficiaryAddress?.address?.trim()) {
        beneficiaryAddressError.address = 'Required';
    }
    if (!beneficiaryAddress?.city?.trim()) {
        beneficiaryAddressError.city = 'Required';
    }
    if (!beneficiaryAddress?.state?.trim()) {
        beneficiaryAddressError.state = 'Required';
    }

    if (!_.isEmpty(beneficiaryAddressError)) {
        wireTransferDetailsError.beneficiaryAddress = beneficiaryAddressError;
    }

    return wireTransferDetailsError;
};

const validateAccountNumber = (details) => {
    const accountNumber = details?.accountNumber;
    const accountNumberConfirm = details?.accountNumberConfirm;
    const error = {};

    // validating account number length (max 32 characters)
    if (accountNumber?.length > 32) {
        error.accountNumber = 'Account Number cannot exceed 32 characters.';
    }

    // validating account numbers confirmation
    if (accountNumber && accountNumber !== accountNumberConfirm) {
        error.accountNumberConfirm = 'Confirm Account Number did not match.';
    }

    return error;
};

export const FormValidator = (formValues) => ({
    callDuration: validateCallDuration(formValues.callDuration),
    consultantInvoiceBankDetails: validateBankDetails(
        formValues.consultantInvoiceBankDetails,
        formValues.consultantCountry
    ),
    consultantInvoiceWireTransferDetails: validateWireTransferDetails(
        formValues.consultantInvoiceWireTransferDetails,
        formValues.consultantCountry
    ),
});
